import React, { useState } from "react";
import api from "../services/api";
import { activeTab, useConsole } from "../lib/console";
import { usePoll } from "../lib/hooks";
import { duration, fmtDateTime, num, plural } from "../lib/format";
import { Badge, Button, Callout, Card, Drawer, ErrorState, KV, Meter, SkeletonRows, Stat } from "../components/ui";
import { Icon } from "../components/Icon";
import { Gauge } from "../components/charts";
import { PageHeader, PageTabs } from "../components/ConsoleShell";

// ── Risk index ──────────────────────────────────────────────────
const RISK_TONE = { MINIMAL: "ok", LOW: "low", MEDIUM: "medium", HIGH: "high", CRITICAL: "critical" };
const toneVar = (t) => (t === "ok" ? "var(--ok)" : `var(--sev-${t})`);
const BANDS = [
  { from: 0, to: 10, color: "var(--ok)" },
  { from: 10, to: 25, color: "var(--sev-low)" },
  { from: 25, to: 50, color: "var(--sev-medium)" },
  { from: 50, to: 75, color: "var(--sev-high)" },
  { from: 75, to: 100, color: "var(--sev-critical)" },
];

function RiskIndex() {
  const c = useConsole();
  const { data, error, reload } = usePoll("/api/risk-score", { interval: 30000 });
  if (error) return <ErrorState detail={error} onRetry={reload} />;
  if (!data) return <Card><SkeletonRows rows={8} /></Card>;
  const tone = RISK_TONE[data.label] || "info";

  return (
    <>
      <div className="grid cols-12">
        <Card className="span-5" title="Cyber risk index" subtitle="0 to 100 · lower is better" icon="gauge">
          <Gauge value={data.score} color={toneVar(tone)} label="risk index" bands={BANDS} />
          <div style={{ textAlign: "center", marginTop: 14 }}>
            <Badge tone={tone} size="lg" dot>
              {data.label.charAt(0) + data.label.slice(1).toLowerCase()} risk
            </Badge>
            <p className="small dim" style={{ marginTop: 10 }}>
              {data.posture}
            </p>
            <p className="tiny muted" style={{ marginTop: 6 }}>
              Computed {fmtDateTime(data.computed_at)} GST · refreshes every 30 s
            </p>
          </div>
        </Card>
        <Card className="span-7" flush title="What drives the index" subtitle="Six weighted factors from the detection database" icon="sliders">
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Factor</th>
                  <th className="num">Weight</th>
                  <th style={{ width: "34%" }}>Factor level</th>
                  <th className="num">Adds</th>
                </tr>
              </thead>
              <tbody>
                {data.breakdown.map((b) => (
                  <tr key={b.name}>
                    <td>
                      <div style={{ fontWeight: 500 }}>{b.name}</div>
                      <div className="tiny muted">{b.detail}</div>
                    </td>
                    <td className="num muted">{b.weight}%</td>
                    <td>
                      <div className="row" style={{ gap: 10 }}>
                        <Meter value={b.raw} tone={b.raw >= 75 ? "critical" : b.raw >= 50 ? "high" : b.raw >= 25 ? "medium" : "ok"} label={`${b.name} level`} />
                        <span className="tnum small muted" style={{ width: 34, textAlign: "right" }}>
                          {Math.round(b.raw)}
                        </span>
                      </div>
                    </td>
                    <td className="num strong">+{b.weighted}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
      <Callout tone="info" icon="info" className="drawer-section" title="Risk index versus security score">
        The risk index (here, lower is better) weighs 24 hours of incidents, open alerts, feed matches, MITRE breadth, failed logins and anomalies. The security score on the Overview
        (higher is better) reflects the last 100 live detections and gives contained incidents less weight.{" "}
        {c.score?.score != null && `It currently reads ${c.score.score}/100.`}
      </Callout>
    </>
  );
}

// ── Compliance ──────────────────────────────────────────────────
const CONTROL_TONE = { met: "ok", partial: "medium", gap: "critical", "n/a": "neutral" };
const CONTROL_LABEL = { met: "Met", partial: "Partial", gap: "Gap", "n/a": "Not applicable" };

function Compliance() {
  const { data, error, reload } = usePoll("/api/compliance");
  const [open, setOpen] = useState(null);
  if (error) return <ErrorState detail={error} onRetry={reload} />;
  if (!data) return <Card><SkeletonRows rows={8} /></Card>;
  const t = data.totals || {};
  const fw = data.frameworks?.find((f) => f.name === open);

  return (
    <>
      <Callout tone="medium" icon="clipboard" title="Self-assessment, not a certification">
        Each control is mapped to what CyberShield implements today, with the evidence shown. Partial controls count as half. This is not an audit or a certification.
      </Callout>
      <div className="grid cols-4" style={{ marginTop: 16 }}>
        <Stat label="Overall coverage" icon="clipboard" tone="brand" value={<>{data.overall_score}<small>%</small></>} sub={`Grade ${data.overall_grade} across ${plural(data.frameworks?.length || 0, "framework")}`} />
        <Stat label="Controls met" icon="checkCircle" tone="ok" value={num(t.met)} sub={`of ${num(t.total)} mapped controls`} />
        <Stat label="Partially met" icon="alertCircle" tone="medium" value={num(t.partial)} sub="Evidence exists, gaps remain" />
        <Stat label="Gaps" icon="x" tone="critical" value={num(t.gap)} sub="Not implemented" />
      </div>
      <div className="grid cols-3" style={{ marginTop: 16 }}>
        {(data.frameworks || []).map((f) => (
          <button key={f.name} type="button" className="stat is-clickable" onClick={() => setOpen(f.name)} style={{ gap: 10 }}>
            <div className="row-between">
              <strong style={{ fontSize: 14.5 }}>{f.name}</strong>
              <Badge tone={f.score >= 80 ? "ok" : f.score >= 60 ? "medium" : "high"}>Grade {f.grade}</Badge>
            </div>
            <span className="small muted">{f.desc}</span>
            <div className="row" style={{ gap: 12 }}>
              <span className="stat-value" style={{ fontSize: 24 }}>
                {f.score}
                <small>%</small>
              </span>
              <div className="grow">
                <Meter value={f.score} tone={f.score >= 80 ? "ok" : f.score >= 60 ? "medium" : "high"} label={`${f.name} coverage`} />
              </div>
            </div>
            <div className="row small" style={{ gap: 14 }}>
              <span style={{ color: "var(--ok)" }}>{f.totals.met} met</span>
              <span style={{ color: "var(--sev-medium)" }}>{f.totals.partial} partial</span>
              {f.totals.gap > 0 && <span style={{ color: "var(--sev-critical)" }}>{f.totals.gap} gaps</span>}
              <span className="muted" style={{ marginLeft: "auto" }}>
                View controls →
              </span>
            </div>
          </button>
        ))}
      </div>
      <Drawer open={!!fw} onClose={() => setOpen(null)} width={680} title={fw?.name} subtitle={fw ? `${fw.desc} · ${fw.score}% · grade ${fw.grade}` : ""} icon={<span className="sev-glyph tone-brand"><Icon name="clipboard" size={17} /></span>}>
        {fw && (
          <div className="list" style={{ margin: "-20px -22px" }}>
            {fw.controls.map((ctl) => (
              <div key={ctl.control} className="list-item" style={{ gridTemplateColumns: "minmax(0,1fr) auto", alignItems: "flex-start", padding: "14px 22px" }}>
                <span style={{ minWidth: 0 }}>
                  <span className="list-title" style={{ whiteSpace: "normal", display: "block" }}>
                    {ctl.control}
                  </span>
                  <span className="small dim" style={{ display: "block", marginTop: 3 }}>
                    {ctl.evidence}
                  </span>
                </span>
                <Badge tone={CONTROL_TONE[ctl.status] || "neutral"} dot>
                  {CONTROL_LABEL[ctl.status] || ctl.status}
                </Badge>
              </div>
            ))}
          </div>
        )}
      </Drawer>
    </>
  );
}

// ── Platform security ───────────────────────────────────────────
const CRYPTO_LABELS = [
  ["encryption_at_rest", "Encryption at rest"],
  ["key_derivation", "Key derivation"],
  ["password_hashing", "Password hashing"],
  ["message_auth", "Message authentication"],
  ["audit_chain", "Audit log integrity"],
  ["key_source", "Master key source"],
  ["csprng", "Random number source"],
];

function AuditIntegrity() {
  const chain = usePoll("/api/audit/chain", { params: { limit: 1 }, interval: 30000 });
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const verify = async () => {
    setBusy(true);
    try {
      const r = await api.get("/api/audit/verify");
      setRes(r.data);
    } catch (e) {
      setRes({ error: e?.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
      chain.reload();
    }
  };
  return (
    <Card title="Audit trail integrity" subtitle="Hash-chained, HMAC-signed audit entries" icon="link">
      <KV
        items={[
          ["Entries in chain", chain.data ? num(chain.data.count) : "—"],
          ["Chain tail", chain.data?.tail_hash ? <span className="mono" title={chain.data.tail_hash}>{String(chain.data.tail_hash).slice(0, 16)}…</span> : "—"],
        ]}
      />
      <div className="row-between row-wrap" style={{ marginTop: 16 }}>
        <span className="small muted" style={{ maxWidth: 360 }}>
          Verification recomputes every link held in memory since the backend started. Persistent entries are also written to the database audit log.
        </span>
        <Button icon="shield" loading={busy} onClick={verify}>
          Verify chain
        </Button>
      </div>
      {res && (
        <div style={{ marginTop: 14 }}>
          {res.error ? (
            <Callout tone="critical" icon="alertCircle">
              {res.error}
            </Callout>
          ) : res.ok ? (
            <Callout tone="ok" icon="checkCircle" title="Chain intact">
              {num(res.entries_checked)} entries verified, no tampering detected.
            </Callout>
          ) : (
            <Callout tone="critical" icon="alert" title="Chain broken">
              Link {res.broken_index} does not match its predecessor.
            </Callout>
          )}
        </div>
      )}
    </Card>
  );
}

function Platform() {
  const crypto = usePoll("/api/security/crypto");
  const mw = usePoll("/api/security/middleware");
  const std = usePoll("/api/security/standards");
  return (
    <div className="grid cols-12">
      <Card className="span-6" title="Cryptography" subtitle="As configured in the backend" icon="key">
        {crypto.error ? (
          <ErrorState detail={crypto.error} onRetry={crypto.reload} />
        ) : !crypto.data ? (
          <SkeletonRows rows={6} />
        ) : (
          <KV items={CRYPTO_LABELS.filter(([k]) => crypto.data[k]).map(([k, l]) => [l, String(crypto.data[k]), { wrap: true }])} />
        )}
      </Card>
      <div className="span-6">
        <AuditIntegrity />
      </div>
      <Card className="span-6" title="Request protection" subtitle="HTTP middleware in front of the API" icon="shield">
        {mw.error ? (
          <ErrorState detail={mw.error} onRetry={mw.reload} />
        ) : !mw.data ? (
          <SkeletonRows rows={6} />
        ) : (
          <div className="col" style={{ gap: 16 }}>
            <div>
              <div className="small muted" style={{ marginBottom: 8 }}>
                Security headers on every response
              </div>
              <div className="row row-wrap" style={{ gap: 6 }}>
                {(mw.data.security_headers || []).map((h) => (
                  <Badge key={h} tone="neutral">
                    {h}
                  </Badge>
                ))}
              </div>
            </div>
            <div>
              <div className="small muted" style={{ marginBottom: 8 }}>
                Input sanitisers
              </div>
              <div className="row row-wrap" style={{ gap: 6 }}>
                {(mw.data.input_sanitizers || []).map((h) => (
                  <Badge key={h} tone="brand">
                    {h.replace(/_/g, " ")}
                  </Badge>
                ))}
              </div>
            </div>
            <div className="grid cols-2" style={{ gap: 16 }}>
              <div>
                <div className="small muted" style={{ marginBottom: 8 }}>
                  Rate limits
                </div>
                {Object.entries(mw.data.rate_limits || {}).map(([k, v]) => (
                  <div key={k} className="row-between small" style={{ padding: "3px 0" }}>
                    <span className="dim">{k}</span>
                    <span className="mono">{v}</span>
                  </div>
                ))}
              </div>
              <div>
                <div className="small muted" style={{ marginBottom: 8 }}>
                  Sign-in lockout
                </div>
                {(mw.data.lockout_schedule || []).map((l) => (
                  <div key={l.failures} className="row-between small" style={{ padding: "3px 0" }}>
                    <span className="dim">{plural(l.failures, "failure")}</span>
                    <span className="mono">{duration(l.seconds)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </Card>
      <Card className="span-6" title="Reference encryption standards" subtitle="Transport and wireless standards the design targets" icon="wifi">
        {std.error ? (
          <ErrorState detail={std.error} onRetry={std.reload} />
        ) : !std.data ? (
          <SkeletonRows rows={6} />
        ) : (
          <>
            <KV
              items={[
                ["Transport", std.data.tls?.version],
                ["Cipher suite", std.data.tls?.cipher_suite, { mono: true, wrap: true }],
                ["Wireless", `${std.data.wireless?.standard} · ${std.data.wireless?.cipher}`],
                ["Key size", `${std.data.wireless?.key_size_bits}-bit (was ${std.data.wireless?.previous?.key_size_bits}-bit)`],
              ]}
            />
            <div className="table-wrap" style={{ marginTop: 16, border: "1px solid var(--line)", borderRadius: "var(--r)" }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>Property</th>
                    <th>WPA2 · CCMP</th>
                    <th>WPA3 · GCMP-256</th>
                  </tr>
                </thead>
                <tbody>
                  {(std.data.comparison || []).map((r) => (
                    <tr key={r.feature}>
                      <td className="muted">{r.feature}</td>
                      <td>{r.wpa2_ccmp}</td>
                      <td style={{ color: "var(--ok)" }}>{r.wpa3_gcmp_256}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Card>
      <div className="span-12">
        <Callout tone="info" icon="info" title="Known limitations">
          Multi-factor sign-in to this console is not implemented yet, the console uses a single shared role, and chain verification covers entries held in memory since the last restart.
        </Callout>
      </div>
    </div>
  );
}

export default function Risk() {
  const c = useConsole();
  const tab = activeTab("risk", c.tab);
  return (
    <>
      <PageHeader
        eyebrow="Protect"
        icon="gauge"
        title="Risk & compliance"
        description="Assess exposure, review compliance, and monitor platform security."
      />
      <PageTabs page="risk" />
      {tab === "risk" && <RiskIndex />}
      {tab === "compliance" && <Compliance />}
      {tab === "platform" && <Platform />}
    </>
  );
}
